import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import api from '../services/api';
import socketService from '../services/socket';

/**
 * งานรอดำเนินการของเจ้าหน้าที่ — แหล่งเดียวสำหรับ badge บนเมนู, แถบบอกทาง (PendingWorkBanner)
 * และป้ายตัวเลขบนการ์ดใน AdminDashboard
 *
 * ดึงจาก GET /admin/pending-summary (server นับเฉพาะหมวดที่ผู้ใช้มีสิทธิ์) รอบเดียวต่อครั้ง
 * รีเฟรชเมื่อ: เปลี่ยนหน้า / socket แจ้ง pending_changed, new_request / มี mutation ผ่าน api (event
 * imoney:pending-refresh จาก services/api.js) / กลับมาโฟกัสแท็บ / ทุก 2 นาที (fallback)
 */

// key ต้องตรงกับ server/utils/pendingSummary.js
export const PENDING_CATALOG = [
    { key: 'doc_pending',      label: 'คำร้องขอเอกสาร',            hint: 'รออนุมัติสลิป / 50 ทวิ / หนังสือรับรอง',      path: '/admin/requests' },
    { key: 'cert_wait_issue',  label: 'หนังสือรับรองรอออก',         hint: 'รับนัดแล้ว ยังไม่ติ๊ก "หนังสือออกแล้ว"',      path: '/admin/requests' },
    { key: 'account_pending',  label: 'คำขอเปิดบัญชี',              hint: 'รอตรวจรูปบัตร / อนุมัติ',                   path: '/admin/account-requests' },
    { key: 'account_awaiting', label: 'บัญชีรอยืนยันเพิ่มเข้าระบบ',   hint: 'รับเรื่องแล้ว รอยืนยันว่าเพิ่มเข้าระบบ',       path: '/admin/account-requests' },
    { key: 'password_reset',   label: 'คำขอรีเซ็ตรหัสผ่าน',         hint: 'ตรวจหลักฐานแล้วเปลี่ยนรหัสผ่านให้ผู้ใช้',       path: '/admin/password-resets' },
    { key: 'position_pending', label: 'คำขอแก้ชื่อตำแหน่ง',          hint: 'ตรวจหลักฐานและยืนยันชื่อตำแหน่ง',            path: '/admin/position-requests' },
    { key: 'contact_new',      label: 'ข้อความจากผู้ใช้',            hint: 'ยังไม่ได้ตอบกลับ',                          path: '/admin/contact' }
];

export const PENDING_REFRESH_EVENT = 'imoney:pending-refresh';

const POLL_MS = 120000;
const DEBOUNCE_MS = 400;

const PendingWorkContext = createContext({
    enabled: false,
    counts: {},
    total: 0,
    items: [],
    loaded: false,
    refresh: () => {},
    countFor: () => 0
});

export function PendingWorkProvider({ children }) {
    const { user, canAccessAdmin } = useAuth();
    const location = useLocation();
    const [counts, setCounts] = useState({});
    const [total, setTotal] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const timerRef = useRef(null);
    const inflightRef = useRef(false);

    const enabled = !!user && canAccessAdmin();

    const fetchNow = useCallback(async () => {
        if (!enabled || inflightRef.current) return;
        inflightRef.current = true;
        try {
            const res = await api.get('/admin/pending-summary');
            setCounts(res.data?.counts || {});
            setTotal(Number(res.data?.total) || 0);
            setLoaded(true);
        } catch (err) {
            // ไม่มีสิทธิ์/ออฟไลน์ — คงค่าเดิมไว้ ไม่ต้องรบกวนผู้ใช้
        } finally {
            inflightRef.current = false;
        }
    }, [enabled]);

    // รวมหลาย trigger ที่มาติด ๆ กันให้เหลือยิงครั้งเดียว
    const refresh = useCallback(() => {
        if (!enabled) return;
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            fetchNow();
        }, DEBOUNCE_MS);
    }, [enabled, fetchNow]);

    // โหลดครั้งแรก + ทุกครั้งที่เปลี่ยนหน้า (หลังปิดเคสแล้วเดินไปหน้าอื่น ตัวเลขต้องลดทันที)
    useEffect(() => {
        if (!enabled) {
            setCounts({});
            setTotal(0);
            setLoaded(false);
            return;
        }
        refresh();
    }, [enabled, location.pathname, refresh]);

    // Socket + mutation event + กลับมาที่แท็บ + poll fallback
    useEffect(() => {
        if (!enabled) return undefined;

        const token = localStorage.getItem('token');
        if (token) socketService.connect(token);

        const onSocket = () => refresh();
        socketService.on('pending_changed', onSocket);
        socketService.on('new_request', onSocket);
        socketService.on('connect', onSocket);

        const onCustom = () => refresh();
        window.addEventListener(PENDING_REFRESH_EVENT, onCustom);

        const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
        document.addEventListener('visibilitychange', onVisible);

        const interval = setInterval(fetchNow, POLL_MS);

        return () => {
            socketService.off('pending_changed', onSocket);
            socketService.off('new_request', onSocket);
            socketService.off('connect', onSocket);
            window.removeEventListener(PENDING_REFRESH_EVENT, onCustom);
            document.removeEventListener('visibilitychange', onVisible);
            clearInterval(interval);
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [enabled, refresh, fetchNow]);

    // รายการหมวดที่มีงานค้าง (เรียงตาม catalog) — ใช้วาด chip ในแถบบอกทาง
    const items = useMemo(
        () => PENDING_CATALOG
            .map(c => ({ ...c, count: Number(counts[c.key]) || 0 }))
            .filter(c => c.count > 0),
        [counts]
    );

    // ยอดรวมของหมวดที่พาไปยัง path นี้ (ใช้ทำ badge เมนู "คำร้อง" ฯลฯ)
    const countFor = useCallback(
        (pathPrefix) => items
            .filter(c => c.path === pathPrefix || c.path.startsWith(pathPrefix + '/'))
            .reduce((sum, c) => sum + c.count, 0),
        [items]
    );

    const value = useMemo(
        () => ({ enabled, counts, total, items, loaded, refresh, countFor }),
        [enabled, counts, total, items, loaded, refresh, countFor]
    );

    return (
        <PendingWorkContext.Provider value={value}>
            {children}
        </PendingWorkContext.Provider>
    );
}

export function usePendingWork() {
    return useContext(PendingWorkContext);
}

export default PendingWorkContext;
